import React from 'react';

/**
 * Select component with error support
 * @param {Object} props
 * @param {string} props.label
 * @param {string} props.error
 * @param {string} props.id
 * @param {React.ReactNode} props.children
 * @param {string} props.className
 */
export const Select = React.forwardRef(({
  label,
  error,
  id,
  children,
  className = '',
  ...props
}, ref) => {
  const hasError = !!error;
  
  return (
    <div className="w-full">
      {label && (
        <label 
          htmlFor={id} 
          className="block text-sm font-medium text-brand-text mb-1.5"
        >
          {label}
          {props.required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={id}
          className={`
            block w-full rounded-xl border bg-brand-bg text-brand-text
            transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-brand-bg
            disabled:opacity-50 disabled:cursor-not-allowed appearance-none
            px-3 py-2 pr-10
            ${hasError 
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
              : 'border-zinc-800 focus:border-brand-gold focus:ring-brand-gold'
            }
            ${className}
          `}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${id}-error` : undefined}
          {...props}
        >
          {children}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-brand-muted" aria-hidden="true">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {error && (
        <p 
          id={`${id}-error`} 
          className="mt-1.5 text-sm text-red-500"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
});

Select.displayName = 'Select';
